import { useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { tripData, getTrip } from "store/tripSlice";
import { timeZone, getTimeline } from "store/vehicleTripSlice";
import {
  Box,
  Paper,
  Stack,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";
import {
  CheckCircle as PassIcon,
  Flag as ReviewIcon,
  AutoFixHigh as AutoFixHighIcon,
  DeleteOutline as DeleteOutlineIcon,
  Schedule as ScheduleIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  CompareArrows as CompareArrowsIcon,
  Place as PlaceIcon,
  SaveAlt as SaveAltIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import moment from "moment";
import "moment-timezone";
import { GetTimeZoneFromAbbreviation, Role } from "common/Constants";
import { AuthContext } from "common/AuthProvider";
import { ACCESS_AUTO_APPROVE_PREVIEW, canApplyAutoClose } from "trips/permissions";
import AutoCloseApplyButton from "./AutoCloseApplyButton";
import ReconstructionService, {
  AutoClosePreviewResponse,
  AutoClosePreviewEvent,
  AutoCloseSelection,
  IntermediateStopDto,
  TripReconstructionResponse,
  ReconciliationSuggestionDto,
  ReconciliationAction,
  RepointSuggestion,
  RemovedWaitInfo,
} from "services/reconstructionService";
import SuggestedEventsService, { SuggestedEventDetail } from "services/suggestedEventsService";

type ChipColor = "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";

interface AutoClosePreviewLayoutProps {
  tripNumber?: string;
  isHidden?: boolean;
  /** Jump the user to the Closer tab — for blockers GPS can't fix (e.g. a reopened trip). */
  onOpenCloser?: () => void;
  /** Fired after a committed apply so the parent can drop other tabs' stale mounts (force reload). */
  onApplied?: () => void;
}

// Friendly label + chip color per GPS stop category (mirrors the GPS Reconstruction tab).
const categoryStyle = (category: string): { label: string; color: ChipColor } => {
  switch (category) {
    case "FuelStop": return { label: "Fuel stop", color: "warning" };
    case "CrewWait": return { label: "Crew wait", color: "info" };
    case "DriverBreak": return { label: "Driver break", color: "default" };
    case "Inspection": return { label: "Vehicle inspection", color: "secondary" };
    case "AssistMovement": return { label: "Assist movement", color: "primary" };
    case "Staging": return { label: "Parked at yard/depot", color: "success" };
    case "OvernightPark": return { label: "Overnight / off-shift park", color: "default" };
    default: return { label: "Unclassified stop", color: "default" };
  }
};

const stopDuration = (mins: number) => {
  const m = Math.round(mins);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 === 0 ? `${h}h` : `${h}h ${m % 60}m`;
};

// Categories where the van merely parked at a yard/depot (never billed) vs a genuine stop.
const isParkedCategory = (c: string) => c === "Staging" || c === "OvernightPark";

// Each GPS wait is persisted as TWO metrics.SuggestedEvent rows — a Wait Start (type 10) and a Wait End
// (type 11) with the same times. The Wait Start row is the canonical one (its start+end times drive the
// whole pair); ignore the Wait End rows so a wait shows once and can't be accepted twice (double-billed).
const WAIT_START_TYPE_ID = 10;

// Amber (re-sourced odometer) and green (inserted from GPS) tints, tuned for the default light theme.
const INSERT_BG = "#e6f3ec";
const CHANGE_BG = "#fbf1dc";
const INSERT_INK = "#137a45";
const CHANGE_INK = "#8a5a10";
const GPS_INK = "#1565c0";
const REMOVE_INK = "#b3261e";
// Row the current blocker points at ("Line N …").
const FLAG_BG = "#fdecea";
const FLAG_INK = "#b3261e";

// Suggestions at/above this confidence are pre-checked, so the recommended fix is one click away.
const AUTO_CHECK_CONFIDENCE = 0.8;

// Shared surface: soft border, rounded, quietly elevated — one consistent card language.
const cardSx = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: "0 1px 2px rgba(16,24,40,0.05), 0 4px 12px rgba(16,24,40,0.04)",
} as const;

// Consistent section heading: an icon tile + title + optional caption.
const SectionHeader = ({ icon, title, caption }: { icon: ReactNode; title: string; caption?: string }) => (
  <Box sx={{ mb: 1.5 }}>
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          color: "text.secondary",
          bgcolor: "action.hover",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: "1.02rem" }}>{title}</Typography>
    </Stack>
    {caption && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 680 }}>
        {caption}
      </Typography>
    )}
  </Box>
);

// Uppercase table-header cells — the quiet, scannable data-grid look.
const headSx = {
  fontWeight: 700,
  whiteSpace: "nowrap",
  bgcolor: "action.hover",
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontSize: "0.66rem",
} as const;

const fmtTime = (t?: string) => (t ? moment(t).format("MM/DD HH:mm") : "—");
const fmtOdo = (o?: number | null) => (o == null ? "—" : o.toLocaleString());
// Display-only: spell "Arr" out as "Arrived" (backend/lookup strings stay "Arr Pickup"/"Arr Dest").
const displayEventName = (n?: string) => (n ? n.replace(/^Arr /, "Arrived ") : n ?? "");
const keyOf = (e: AutoClosePreviewEvent) => `${e.eventTypeName}|${moment(e.timeLocal).valueOf()}`;
// Stable key for an "add" suggestion (it has no driver EventId).
const addKeyOf = (s: ReconciliationSuggestionDto) => `${s.eventType}|${s.suggestedTimeUtc ?? ""}`;

// bot-event short names (@evName in the preview SQL) → numeric EventType, so a repaired-timeline row can
// be tied back to a reconciliation suggestion (which carries the numeric type, not the short name).
const SHORT_NAME_TO_TYPE: Record<string, number> = {
  "Arr Pickup": 2,
  "Depart w/Crew": 4,
  "Arr Dest": 5,
  "Drop Off": 7,
};

// Stable string identity of a selection — used for the "up to date vs re-preview" (dirty) check.
const selKey = (rm: Set<number>, rt: Set<number>, ad: Set<string>, rp: Set<number>) =>
  JSON.stringify({ r: [...rm].sort(), t: [...rt].sort(), a: [...ad].sort(), p: [...rp].sort() });

// The single builder that turns the checkbox sets into the API selection. Used both for the live
// `selection` and for the initial default in the load effect — one source of truth (no drift).
const makeSelection = (
  list: ReconciliationSuggestionDto[],
  rm: Set<number>,
  rt: Set<number>,
  ad: Set<string>,
  repointSugs: RepointSuggestion[],
  rp: Set<number>
): AutoCloseSelection => ({
  removeEventIds: [...rm],
  retimes: list
    .filter((s) => s.action === "retime" && s.driverEventId != null && s.suggestedTimeUtc && rt.has(s.driverEventId))
    .map((s) => ({ eventId: s.driverEventId as number, newTimeUtc: s.suggestedTimeUtc as string, origTimeUtc: s.driverTimeUtc })),
  adds: list
    .filter((s) => s.action === "add" && s.suggestedTimeUtc && ad.has(addKeyOf(s)))
    .map((s) => ({ eventType: s.eventType, timeUtc: s.suggestedTimeUtc as string, latitude: s.latitude, longitude: s.longitude })),
  repoints: repointSugs
    .filter((s) => rp.has(s.eventId))
    .map((s) => ({ eventId: s.eventId, routeLocation: s.newRouteLocation })),
});

const AutoClosePreviewLayout = ({ isHidden, onOpenCloser, onApplied }: AutoClosePreviewLayoutProps) => {
  const dispatch = useDispatch();
  // Whoever sees the preview may apply it: Admin or the AccessAutoApprovePreview permission.
  const { isUserInRole, userHasPermission } = useContext(AuthContext);
  const canApply = canApplyAutoClose(isUserInRole(Role.Admin), userHasPermission(ACCESS_AUTO_APPROVE_PREVIEW));
  const trip = useSelector(tripData);
  const _timeZone = useSelector(timeZone);
  const tz = useMemo(() => GetTimeZoneFromAbbreviation(_timeZone), [_timeZone]);
  const [preview, setPreview] = useState<AutoClosePreviewResponse | null>(null);
  const [recon, setRecon] = useState<TripReconstructionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The closer's current selection of corrections to apply, split by action. High-confidence ones start
  // checked so the recommended fix is already staged — the closer just previews or applies.
  const [removeIds, setRemoveIds] = useState<Set<number>>(new Set());
  const [retimeIds, setRetimeIds] = useState<Set<number>>(new Set());
  const [addKeys, setAddKeys] = useState<Set<string>>(new Set());
  // Re-point checkboxes (by driver EventId). Off by default — a RouteLocation change is billing-semantic,
  // so the closer opts in per event; the preview shows the mismatch as a blocker until they do.
  const [repointIds, setRepointIds] = useState<Set<number>>(new Set());

  // Which selection the currently-shown preview reflects, so we can tell "up to date" from "re-preview".
  const [appliedKey, setAppliedKey] = useState<string>("");
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);

  // Apply (commit) state.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // The trip's GPS wait suggestions (from the closer's own pipeline).
  const [waitSuggestions, setWaitSuggestions] = useState<SuggestedEventDetail[]>([]);
  // Per suggestion: accept (create the Wait pair) with a reason the closer picked. Keyed by suggestion id.
  const [waitDecisions, setWaitDecisions] = useState<Record<number, { accept: boolean; reason: string }>>({});

  // Client wait reasons come from the preview (with their Billable flag). A mid-trip wait's reason must be
  // billable (a non-billable one blocks approval), so the dropdown for a mid-trip stop offers billable
  // reasons only — plus Fuel Stop when the stop is over 15 min (the approver's own fuel exemption).
  const billableReasons = useMemo(() => (preview?.waitReasons ?? []).filter((r) => r.billable).map((r) => r.name), [preview]);
  const fuelReasons = useMemo(
    () => (preview?.waitReasons ?? []).filter((r) => !r.billable && /fuel/i.test(r.name)).map((r) => r.name),
    [preview]
  );

  const suggestions = recon?.reconciliation ?? [];
  const repointSuggestions = useMemo(() => preview?.repointSuggestions ?? [], [preview]);

  // Build the API selection from the current checkbox state (one shared builder — see makeSelection).
  const selection = useMemo<AutoCloseSelection>(
    () => makeSelection(suggestions, removeIds, retimeIds, addKeys, repointSuggestions, repointIds),
    [suggestions, removeIds, retimeIds, addKeys, repointSuggestions, repointIds]
  );

  const selectionKey = useMemo(
    () => selKey(removeIds, retimeIds, addKeys, repointIds),
    [removeIds, retimeIds, addKeys, repointIds]
  );
  const dirty = selectionKey !== appliedKey;
  const selectedCount = removeIds.size + retimeIds.size + addKeys.size + repointIds.size;
  const committed = preview?.committed === true;

  // Default selection = high-confidence REMOVES only. Re-points are auto-applied separately (once the first
  // preview surfaces them — see the load effect). RE-TIMES are deliberately NOT auto-checked: a 60-trip flip
  // measurement showed auto-applying anchor re-times was net-negative (every regression traced to a re-time —
  // it opens >Google time gaps, inflates the destination dwell, and inverts the dest pair), while re-points
  // were the clean win. The driver's logged TIMES generally satisfy the approver's timing guardrails better
  // than GPS "truth" does, so re-times stay a manual opt-in the closer applies case-by-case (the pickup-pair
  // guard in the apply still protects any re-time they do check). Adds also stay opt-in.
  const defaultSelection = (r: TripReconstructionResponse | null) => {
    const rm = new Set<number>();
    (r?.reconciliation ?? []).forEach((s) => {
      if (s.action === "remove" && s.driverEventId != null && s.confidence >= AUTO_CHECK_CONFIDENCE) rm.add(s.driverEventId);
    });
    return { rm, rt: new Set<number>(), ad: new Set<string>() };
  };

  useEffect(() => {
    if (!trip?.id) return;
    const id = trip.id;
    setLoading(true);
    setError(null);
    setApplyError(null);

    // GPS wait suggestions (this also generates them on first look). Wait reasons come with the preview.
    SuggestedEventsService.getTripSuggestedEvents(id)
      .then((list) => setWaitSuggestions((list || []).filter((s) => s.suggestedEventTypeId === WAIT_START_TYPE_ID)))
      .catch(() => setWaitSuggestions([]));

    // Reconstruct first (to know what to recommend), then preview WITH the recommended fixes already
    // applied — so the projected outcome is visible on open, in one load, no extra click.
    ReconstructionService.reconstructTrip(id)
      .then((r) => r, () => null)
      .then((r) => {
        setRecon(r);
        const { rm, rt, ad } = defaultSelection(r);
        const rp = new Set<number>();
        setRemoveIds(rm);
        setRetimeIds(rt);
        setAddKeys(ad);
        setRepointIds(rp);
        setAppliedKey(selKey(rm, rt, ad, rp));
        return ReconstructionService.getAutoClosePreview(id, makeSelection(r?.reconciliation ?? [], rm, rt, ad, [], rp));
      })
      .then((p) => {
        setPreview(p);
        // Re-points are only known AFTER the first preview (the SQL batch derives them from GPS proximity to
        // the dispatched point-of-service), so they can't go through defaultSelection. Auto-apply every
        // GPS-confirmed re-point on initial load — the debounce below re-previews with them folded in. This
        // runs only on trip load (manual re-runs use rerunPreview), so it never fights the closer's unchecks.
        const rpIds = (p?.repointSuggestions ?? []).map((s) => s.eventId);
        if (rpIds.length > 0) setRepointIds(new Set(rpIds));
      })
      .catch(() => setError("Could not build the auto-close preview. Please try again."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id]);

  // Re-run the projection (rolled back) with the currently-checked corrections.
  const rerunPreview = () => {
    if (!trip?.id) return;
    setRerunning(true);
    setRerunError(null);
    ReconstructionService.getAutoClosePreview(trip.id, selection)
      .then((p) => {
        setPreview(p);
        setAppliedKey(selectionKey);
      })
      .catch(() => setRerunError("Couldn't re-check with these fixes — please try again."))
      .finally(() => setRerunning(false));
  };

  // Keep the grid + outcome in lock-step with the checkboxes: whenever the selection differs from what's
  // shown, re-preview automatically (debounced). So unchecking a remove brings its event straight back,
  // and checking a re-time/add shows its effect — no separate "preview" click, no stale grid.
  useEffect(() => {
    if (!trip?.id || loading || committed || rerunning) return;
    if (selectionKey === appliedKey) return; // already in sync
    const t = setTimeout(() => rerunPreview(), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, appliedKey, committed, loading, rerunning, trip?.id]);

  // Commit the corrections for real: writes, runs the approver, records the apply.
  const applyChanges = () => {
    if (!trip?.id || !canApply) return;
    setApplying(true);
    setApplyError(null);
    const payload: AutoCloseSelection = {
      ...selection,
      waitAccepts: waitPayload.accepts,
      waitDismissSuggestionIds: waitPayload.dismisses,
    };
    ReconstructionService.applyAutoClose(trip.id, payload)
      .then((p) => {
        setPreview(p);
        setAppliedKey(selectionKey);
        setConfirmOpen(false);
        // The apply wrote to the trip (repaired events + approval). Refresh the main trip details in place
        // so the new status shows without a full browser reload: getTrip reloads the trip record (header,
        // Approved By / Closed By, isApproved), and getTimeline reloads the event timeline the summary reads
        // for Miles/Minutes — keyed on trip.id, the same key TripSummary uses (its own effect won't re-fire
        // because trip.id is unchanged). This component's load effect is likewise keyed on trip.id, so the
        // committed preview stays intact.
        if (p?.committed) {
          if (trip.tripNumber != null) dispatch(getTrip(trip.tripNumber.toString()));
          if (trip.id != null) dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: "active" }));
          // Tell the parent to drop the other tabs' cached mounts so they re-fetch fresh (they stay
          // mounted via visitedTabs and would otherwise show pre-apply data until a full page reload).
          onApplied?.();
        }
      })
      .catch(() => setApplyError("Couldn't apply the changes — nothing was saved. Please try again."))
      .finally(() => setApplying(false));
  };

  const toggleRemove = (id: number) =>
    setRemoveIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleRetime = (id: number) =>
    setRetimeIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAdd = (k: string) =>
    setAddKeys((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleRepoint = (id: number) =>
    setRepointIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // "Select all / Clear all" over every proposed change (removes, re-times, adds, re-points) at once.
  const totalSuggestionCount = useMemo(
    () =>
      suggestions.filter((s) => (s.action === "add" ? !!s.suggestedTimeUtc : s.driverEventId != null)).length +
      repointSuggestions.length,
    [suggestions, repointSuggestions]
  );
  const allChecked = totalSuggestionCount > 0 && selectedCount >= totalSuggestionCount;
  const setAllChecked = (check: boolean) => {
    if (check) {
      setRemoveIds(new Set(suggestions.filter((s) => s.action === "remove" && s.driverEventId != null).map((s) => s.driverEventId as number)));
      setRetimeIds(new Set(suggestions.filter((s) => s.action === "retime" && s.driverEventId != null).map((s) => s.driverEventId as number)));
      setAddKeys(new Set(suggestions.filter((s) => s.action === "add" && s.suggestedTimeUtc).map((s) => addKeyOf(s))));
      setRepointIds(new Set(repointSuggestions.map((s) => s.eventId)));
    } else {
      setRemoveIds(new Set());
      setRetimeIds(new Set());
      setAddKeys(new Set());
      setRepointIds(new Set());
    }
  };

  const isChecked = (s: ReconciliationSuggestionDto) => {
    if (s.action === "remove") return s.driverEventId != null && removeIds.has(s.driverEventId);
    if (s.action === "retime") return s.driverEventId != null && retimeIds.has(s.driverEventId);
    return addKeys.has(addKeyOf(s));
  };
  const toggle = (s: ReconciliationSuggestionDto) => {
    if (s.action === "remove" && s.driverEventId != null) toggleRemove(s.driverEventId);
    else if (s.action === "retime" && s.driverEventId != null) toggleRetime(s.driverEventId);
    else if (s.action === "add") toggleAdd(addKeyOf(s));
  };

  // Visual treatment + verb per action.
  const actionStyle = (action: ReconciliationAction) => {
    switch (action) {
      case "remove": return { color: REMOVE_INK, verb: "Remove", icon: <DeleteOutlineIcon fontSize="small" /> };
      case "retime": return { color: CHANGE_INK, verb: "Re-time", icon: <ScheduleIcon fontSize="small" /> };
      case "add":
      default: return { color: INSERT_INK, verb: "Add", icon: <AddCircleOutlineIcon fontSize="small" /> };
    }
  };

  const fmtSug = (utc?: string) => (utc ? `${moment.utc(utc).tz(tz).format("MM/DD HH:mm")} ${_timeZone}` : "—");

  // Plain-language "what this does", spelled out so the closer can decide with full context.
  const effectOf = (s: ReconciliationSuggestionDto): string => {
    if (s.action === "remove")
      return "Removes this event from the trip — GPS doesn't back it up.";
    if (s.action === "add")
      return `Adds a ${displayEventName(s.eventTypeName).toLowerCase()} at ${fmtSug(s.suggestedTimeUtc)} that the driver never logged. It shows with a blank driver column below.`;
    // retime — only ever an anchor (arrive/depart), never a wait, so no payroll effect.
    return `Moves the logged ${displayEventName(s.eventTypeName).toLowerCase()} from ${fmtSug(s.driverTimeUtc)} to the GPS time (${fmtSug(s.suggestedTimeUtc)}). This changes travel-speed and on-time checks only — it does not change any billed wait time.`;
  };

  // GPS stops the engine detected, chronological.
  const stops = useMemo<IntermediateStopDto[]>(
    () => [...(recon?.intermediateStops ?? [])].sort((a, b) => (a.startUtc || "").localeCompare(b.startUtc || "")),
    [recon]
  );
  const fmtStopTime = (utc?: string) => (utc ? `${moment.utc(utc).tz(tz).format("MM/DD HH:mm")} ${_timeZone}` : "—");

  const offsetMs = useMemo(() => {
    const begin = (preview?.driverEvents ?? []).find((e) => e.eventTypeName === "Begin");
    if (!recon?.beginTripUtc || !begin) return null;
    return moment.utc(recon.beginTripUtc).valueOf() - moment.utc(begin.timeLocal).valueOf();
  }, [recon, preview]);

  const waitWindows = useMemo(() => {
    const ev = [...(preview?.driverEvents ?? [])].sort(
      (a, b) => moment.utc(a.timeLocal).valueOf() - moment.utc(b.timeLocal).valueOf()
    );
    const windows: { start: number; end: number }[] = [];
    let open: number | null = null;
    for (const e of ev) {
      if (e.eventTypeName === "Wait Start") open = moment.utc(e.timeLocal).valueOf();
      else if (e.eventTypeName === "Wait End" && open != null) {
        windows.push({ start: open, end: moment.utc(e.timeLocal).valueOf() });
        open = null;
      }
    }
    return windows;
  }, [preview]);

  const stopStatus = (s: IntermediateStopDto): "logged" | "missed" | null => {
    if (offsetMs == null || !s.startUtc) return null;
    const start = moment.utc(s.startUtc).valueOf() - offsetMs;
    const end = moment.utc(s.endUtc || s.startUtc).valueOf() - offsetMs;
    const tol = 5 * 60 * 1000;
    return waitWindows.some((w) => w.start <= end + tol && w.end >= start - tol) ? "logged" : "missed";
  };

  // Mid-trip = between the crew pickup (Depart w/Crew) and the destination (Arr Dest), local-naive ms.
  const midTripWindow = useMemo(() => {
    const bot = preview?.botEvents ?? [];
    const dep = bot.find((e) => e.eventTypeName === "Depart w/Crew");
    const arr = [...bot].reverse().find((e) => e.eventTypeName === "Arr Dest");
    if (!dep || !arr) return null;
    return { start: moment.utc(dep.timeLocal).valueOf(), end: moment.utc(arr.timeLocal).valueOf() };
  }, [preview]);

  // The trip's PENDING wait suggestions (from the closer's own pipeline), enriched with my classifier's
  // category + a mid-trip flag, and flagged "offerable" (a real wait to accept/dismiss) vs informational
  // (already covered by a driver wait, or the van merely parked at a yard off the loaded leg).
  const waitItems = useMemo(() => {
    const off = offsetMs ?? 0;
    const tol = 5 * 60 * 1000;
    return waitSuggestions.map((s) => {
      const sUtcStart = moment.utc(s.suggestedStartTimeUtc).valueOf();
      const sUtcEnd = moment.utc(s.suggestedEndTimeUtc).valueOf();
      const startMs = sUtcStart - off; // local-naive basis (matches botEvents + waitWindows)
      const endMs = sUtcEnd - off;
      const stop = (recon?.intermediateStops ?? []).find((st) => {
        const a = moment.utc(st.startUtc).valueOf();
        const b = moment.utc(st.endUtc || st.startUtc).valueOf();
        return a <= sUtcEnd && b >= sUtcStart;
      });
      const category = stop?.category ?? "Unknown";
      const durationMin = Number(s.originalDurationMinutes) || 0;
      const midTrip = midTripWindow ? startMs >= midTripWindow.start && startMs <= midTripWindow.end : false;
      const covered = waitWindows.some((w) => w.start <= endMs + tol && w.end >= startMs - tol);
      // Only MID-TRIP stops (on the loaded leg) are offerable as waits — GPS can't tell a crew stop from a
      // driver stop, so the closer picks a BILLABLE reason (billable is required mid-trip; a non-billable
      // one blocks approval). Fuel Stop is allowed too when the stop is over 15 min (approver exemption).
      // Off-leg stops (parked at a yard before pickup / after dropoff) stay informational.
      const reasonChoices = [...billableReasons, ...(durationMin > 15 ? fuelReasons : [])];
      const offerable = s.status === "Pending" && !covered && midTrip && reasonChoices.length > 0;
      return { s, startMs, category, midTrip, covered, offerable, durationMin, reasonChoices, confidence: stop?.confidence };
    });
  }, [waitSuggestions, recon, offsetMs, waitWindows, midTripWindow, billableReasons, fuelReasons]);

  // Recon stops with no matching suggestion — shown as informational bands (my detector saw a stop the
  // closer's generator didn't create a suggestion for; nothing to accept, but worth surfacing).
  const unmatchedStops = useMemo(() => {
    return (recon?.intermediateStops ?? []).filter((st) => {
      const a = moment.utc(st.startUtc).valueOf();
      const b = moment.utc(st.endUtc || st.startUtc).valueOf();
      return !waitSuggestions.some((s) => {
        const ss = moment.utc(s.suggestedStartTimeUtc).valueOf();
        const se = moment.utc(s.suggestedEndTimeUtc).valueOf();
        return a <= se && b >= ss;
      });
    });
  }, [recon, waitSuggestions]);

  // Each newly-seen offerable wait starts UNCHECKED — GPS can't tell whose stop it was, so the approver
  // still has to opt in. The REASON is pre-filled from the API's wait-reason rules (POI first, then the
  // trip's legs) so the closer isn't picking from a blank dropdown on every suggestion; it's only taken
  // when this stop's dropdown actually offers it, since a mid-trip wait's reason must be billable and a
  // value the list doesn't contain would render blank and fail on save.
  useEffect(() => {
    setWaitDecisions((prev) => {
      let changed = false;
      const next = { ...prev };
      waitItems.forEach((w) => {
        if (w.offerable && next[w.s.id] === undefined) {
          const suggested = w.s.suggestedWaitReason ?? "";
          next[w.s.id] = { accept: false, reason: w.reasonChoices.includes(suggested) ? suggested : "" };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [waitItems]);

  const setWaitAccept = (id: number, accept: boolean) =>
    setWaitDecisions((p) => ({ ...p, [id]: { accept, reason: p[id]?.reason ?? "" } }));
  // Picking a reason auto-accepts the wait (one click); clearing it un-accepts.
  const setWaitReason = (id: number, reason: string) =>
    setWaitDecisions((p) => ({ ...p, [id]: { accept: reason !== "", reason } }));

  // What Save will do with the wait suggestions: accept the checked ones (create a Wait pair with the
  // chosen reason), dismiss the unchecked ones — both recorded the same way the closer's are.
  const waitPayload = useMemo(() => {
    const accepts: { suggestedEventId: number; reason: string }[] = [];
    const dismisses: number[] = [];
    waitItems.forEach((w) => {
      if (!w.offerable) return;
      const d = waitDecisions[w.s.id];
      // Only accept with a chosen billable reason; anything else the approver leaves alone is dismissed.
      if (d?.accept && d.reason) accepts.push({ suggestedEventId: w.s.id, reason: d.reason });
      else if (d) dismisses.push(w.s.id);
    });
    return { accepts, dismisses };
  }, [waitItems, waitDecisions]);

  const driverByKey = useMemo(() => {
    const m = new Map<string, AutoClosePreviewEvent>();
    (preview?.driverEvents ?? []).forEach((e) => m.set(keyOf(e), e));
    return m;
  }, [preview]);

  const flaggedLines = useMemo(() => {
    const s = new Set<number>();
    const re = /line\s+(\d+)/gi;
    let m: RegExpExecArray | null;
    const text = preview?.outcome?.failReason ?? "";
    while ((m = re.exec(text)) !== null) s.add(Number(m[1]));
    return s;
  }, [preview]);

  // Checked re-times, keyed by numeric EventType, so the repaired-timeline row for that event can show
  // the driver's ORIGINAL time (struck) beside the GPS time it moved to. Without this, a moved row's
  // "driver time" would just echo the new time and hide what actually changed.
  const retimeByType = useMemo(() => {
    const m = new Map<number, ReconciliationSuggestionDto>();
    suggestions.forEach((s) => {
      if (s.action === "retime" && s.driverEventId != null && s.driverTimeUtc && retimeIds.has(s.driverEventId))
        m.set(s.eventType, s);
    });
    return m;
  }, [suggestions, retimeIds]);
  const fmtLocalFromUtc = (utc?: string) => (utc ? moment.utc(utc).tz(tz).format("MM/DD HH:mm") : "—");

  // ONE chronological timeline: the repaired driver/GPS events interleaved with every stop GPS saw along
  // the route (converted to the trip's local clock via the same offset stopStatus uses), so the closer
  // reads what happened in order — no separate stops table.
  const timelineRows = useMemo(() => {
    const off = offsetMs ?? 0;
    const evRows = (preview?.botEvents ?? []).map((ev) => ({
      kind: "event" as const,
      ev,
      sortMs: moment.utc(ev.timeLocal).valueOf(),
    }));
    const waitRows = waitItems.map((w) => ({
      kind: "wait" as const,
      wait: w,
      sortMs: w.startMs,
    }));
    const stopRows = unmatchedStops.map((stop) => ({
      kind: "stop" as const,
      stop,
      sortMs: moment.utc(stop.startUtc).valueOf() - off,
    }));
    // Waits the apply dropped because a re-timed anchor split them — shown in place, struck out.
    const removedRows = (preview?.removedWaits ?? []).map((rw) => ({
      kind: "removed" as const,
      removed: rw,
      sortMs: moment.utc(rw.startLocal).valueOf(),
    }));
    return [...evRows, ...waitRows, ...stopRows, ...removedRows].sort((a, b) => a.sortMs - b.sortMs);
  }, [preview, waitItems, unmatchedStops, offsetMs]);

  if (isHidden) return null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6} gap={2}>
        <CircularProgress size={22} />
        <Typography color="text.secondary">Repairing the trip and checking the auto-approver…</Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
  if (!preview) return null;

  if (!preview.predicted) {
    return (
      <Box p={1}>
        <Alert severity="info" sx={{ mb: 1 }}>
          {preview.note || "This trip can't be GPS-repaired, so an auto-close preview isn't available."}
        </Alert>
      </Box>
    );
  }

  const pass = preview.outcome?.wouldAutoClose === true;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1240, mx: "auto" }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
          Auto-Close Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
          What the driver logged versus what GPS says really happened. Pick the GPS-backed fixes, see whether
          the trip would clear the auto-approver, then apply them to close it without a closer.
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        {/* Outcome hero */}
        <Paper
          elevation={0}
          sx={{ ...cardSx, overflow: "hidden", borderColor: pass ? "rgba(19,122,69,0.35)" : "rgba(138,90,16,0.35)" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2.5,
              background: pass
                ? `linear-gradient(135deg, ${INSERT_BG} 0%, rgba(19,122,69,0.02) 100%)`
                : `linear-gradient(135deg, ${CHANGE_BG} 0%, rgba(138,90,16,0.02) 100%)`,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 54,
                height: 54,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                color: "#fff",
                backgroundColor: pass ? INSERT_INK : CHANGE_INK,
                boxShadow: `0 6px 16px ${pass ? "rgba(19,122,69,0.35)" : "rgba(138,90,16,0.30)"}`,
              }}
            >
              {pass ? <PassIcon sx={{ fontSize: 30 }} /> : <ReviewIcon sx={{ fontSize: 28 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.25, color: pass ? INSERT_INK : CHANGE_INK }}>
                {committed
                  ? pass
                    ? "Approved — closed without a closer"
                    : "Changes saved — still needs the closer"
                  : pass
                    ? "Would auto-close — no closer needed"
                    : "Repaired, but needs one human decision"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25, color: pass ? INSERT_INK : CHANGE_INK, opacity: 0.92 }}>
                {pass ? (
                  committed
                    ? "The fixes were saved and the auto-approver approved the trip."
                    : "Every blocker the GPS fixes can reach is cleared. Apply them to close it."
                ) : (
                  <>
                    Remaining blocker: <b>{preview.outcome?.failReason || preview.approveBlockReason || "unknown"}</b>
                    {flaggedLines.size > 0 && " — highlighted in the timeline below."}
                  </>
                )}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                <Chip size="small" variant="outlined" label={`Vehicle ${trip?.vehicle?.unitNumber ?? "—"}`} />
                {committed && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<SaveAltIcon />}
                    label={`Saved${preview.applyId ? ` · #${preview.applyId}` : ""}`}
                    sx={{ color: GPS_INK, borderColor: "rgba(21,101,192,0.5)", "& .MuiChip-icon": { color: GPS_INK } }}
                  />
                )}
                {selectedCount > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<AutoFixHighIcon />}
                    label={`${selectedCount} GPS fix${selectedCount === 1 ? "" : "es"} ${committed ? "applied" : "staged"}`}
                    sx={{ color: GPS_INK, borderColor: "rgba(21,101,192,0.5)", "& .MuiChip-icon": { color: GPS_INK } }}
                  />
                )}
              </Stack>
              {!pass && onOpenCloser && (
                <Button
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={onOpenCloser}
                  sx={{ mt: 1, px: 0, color: CHANGE_INK, fontWeight: 700 }}
                >
                  This one isn't GPS-fixable — open it in the Closer
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Won't-auto-approve-regardless notice — structural blockers GPS can't touch. Reported before Apply. */}
        {preview.approveBlockReason && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2.5, borderColor: "rgba(179,38,30,0.4)", bgcolor: FLAG_BG }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <ReviewIcon sx={{ color: FLAG_INK, mt: "2px" }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: FLAG_INK }}>
                  This trip won't auto-approve — even fully repaired
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: FLAG_INK }}>
                  {preview.approveBlockReason}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  You can still apply the GPS fixes to clean up the events, but closing it needs a person.
                </Typography>
                {onOpenCloser && (
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    endIcon={<OpenInNewIcon />}
                    onClick={onOpenCloser}
                    sx={{ mt: 1.25, bgcolor: FLAG_INK, "&:hover": { bgcolor: "#8f1e17" }, fontWeight: 700 }}
                  >
                    Approve in the Closer
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        {/* Why it didn't auto-approve */}
        {preview.originalFailReasons && preview.originalFailReasons.length > 0 && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
            <SectionHeader
              icon={<ReviewIcon fontSize="small" />}
              title="Why it didn't auto-approve"
              caption="From the auto-approver's last run on the trip as the driver left it — every blocker, not just the first."
            />
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {preview.originalFailReasons.map((r, i) => (
                <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
                  <Box sx={{ mt: "7px", width: 6, height: 6, borderRadius: "50%", bgcolor: CHANGE_INK, flexShrink: 0 }} />
                  <Typography variant="body2">{r}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Proposed GPS corrections — the toggle surface */}
        {(suggestions.length > 0 || repointSuggestions.length > 0) && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2.5, borderColor: "rgba(21,101,192,0.28)" }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <SectionHeader
                icon={<AutoFixHighIcon fontSize="small" />}
                title="Proposed changes from GPS"
                caption={
                  committed
                    ? "The changes that were applied to this trip."
                    : "GPS-backed fixes. Removes are checked by default; re-times, adds, and re-points are optional — toggle any and the grid + outcome below update automatically. A re-point ties an event the driver app mis-tagged to the point GPS confirms the van was at. Nothing is saved until you apply."
                }
              />
              {!committed && totalSuggestionCount > 1 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setAllChecked(!allChecked)}
                  sx={{ flexShrink: 0, mt: 0.25, fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {allChecked ? "Clear all" : "Select all"}
                </Button>
              )}
            </Stack>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              {suggestions.map((s: ReconciliationSuggestionDto, i: number) => {
                const a = actionStyle(s.action);
                const checked = isChecked(s);
                return (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.25,
                      borderTop: i ? "1px solid" : "none",
                      borderColor: "divider",
                      backgroundColor: checked ? "rgba(21,101,192,0.05)" : "transparent",
                      transition: "background-color 120ms ease",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={committed}
                      onChange={() => toggle(s)}
                      sx={{ p: 0.25 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25, flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={a.icon}
                          label={a.verb}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            color: a.color,
                            borderColor: a.color,
                            "& .MuiChip-icon": { color: a.color, ml: "4px" },
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {displayEventName(s.eventTypeName)}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${Math.round((s.confidence ?? 0) * 100)}% sure`}
                          sx={{ height: 20, fontSize: "0.66rem", color: "text.secondary" }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.primary" sx={{ display: "block" }}>
                        {effectOf(s)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        {s.reason}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {repointSuggestions.map((s: RepointSuggestion, i: number) => {
                const checked = repointIds.has(s.eventId);
                const first = suggestions.length === 0 && i === 0;
                return (
                  <Box
                    key={`rp-${s.eventId}`}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.25,
                      borderTop: first ? "none" : "1px solid",
                      borderColor: "divider",
                      backgroundColor: checked ? "rgba(21,101,192,0.05)" : "transparent",
                      transition: "background-color 120ms ease",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={committed}
                      onChange={() => toggleRepoint(s.eventId)}
                      sx={{ p: 0.25 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25, flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<PlaceIcon fontSize="small" />}
                          label="Re-point"
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            color: CHANGE_INK,
                            borderColor: CHANGE_INK,
                            "& .MuiChip-icon": { color: CHANGE_INK, ml: "4px" },
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {displayEventName(s.eventTypeName)}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${Math.round(s.nearMeters)} m from point`}
                          sx={{ height: 20, fontSize: "0.66rem", color: "text.secondary" }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.primary" sx={{ display: "block" }}>
                        {s.currentName ?? "Unknown point"} → <b>{s.newName ?? "dispatched point"}</b>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        Driver app tagged the wrong point; GPS puts the van at the dispatched location.
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {!committed && (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                <AutoCloseApplyButton
                  canApply={canApply}
                  applying={applying}
                  disabled={applying || rerunning || dirty}
                  onClick={() => setConfirmOpen(true)}
                />
                <Typography
                  variant="caption"
                  color={rerunError || applyError ? "error" : "text.secondary"}
                  sx={{ flex: "1 1 220px", display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  {(rerunning || dirty) && !applyError && !rerunError && <CircularProgress size={12} color="inherit" />}
                  {applyError
                    ? applyError
                    : rerunError
                      ? rerunError
                      : rerunning || dirty
                        ? "Updating preview…"
                        : pass
                          ? canApply
                            ? "Outcome above reflects these fixes. Apply to close the trip."
                            : "Outcome above reflects these fixes. An Admin or Auto-Approve Preview user can apply them to close the trip."
                          : "Outcome above reflects these fixes."}
                </Typography>
              </Stack>
            )}
          </Paper>
        )}

        {/* Driver vs GPS event diff (repaired timeline — incorporates the applied adds as blank-driver rows) */}
        <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
          <SectionHeader
            icon={<CompareArrowsIcon fontSize="small" />}
            title="Driver vs GPS"
            caption={
              "One chronological timeline — the driver's events (with GPS repairs) interleaved with every stop GPS saw. " +
              "Green rows were recovered from GPS; amber rows were moved or had odometers re-sourced. Only mid-trip stops can be turned into a Wait pair — pick a billable reason (GPS can't tell a crew stop from a driver stop, and a non-billable wait mid-trip would block approval). Stops off the loaded leg aren't billed." +
              (rerunning || dirty ? " Updating…" : "")
            }
          />
          <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, opacity: rerunning || dirty ? 0.5 : 1, transition: "opacity 150ms ease" }}>
            <Table size="small" sx={{ "& td, & th": { fontSize: "0.8rem", borderColor: "divider" } }}>
              <TableHead>
                <TableRow sx={{ "& th": headSx }}>
                  <TableCell align="right" sx={{ width: 40 }}>#</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell align="right">Driver time</TableCell>
                  <TableCell align="right">Driver odo</TableCell>
                  <TableCell>Driver loc</TableCell>
                  <TableCell align="right" sx={{ borderLeft: "1px solid", borderColor: "divider" }}>GPS time</TableCell>
                  <TableCell align="right">GPS odo</TableCell>
                  <TableCell>GPS loc</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timelineRows.map((row, i) => {
                  if (row.kind === "wait") {
                    const w = row.wait;
                    const s = w.s;
                    const c = categoryStyle(w.category);
                    const dec = waitDecisions[s.id];
                    const accepted = s.status === "Accepted";
                    const dismissedAlready = s.status === "Dismissed";
                    const bg = accepted
                      ? INSERT_BG
                      : w.offerable
                        ? dec?.accept ? "rgba(21,101,192,0.06)" : CHANGE_BG
                        : "rgba(0,0,0,0.025)";
                    // Mid-trip waits may only take a BILLABLE reason (a non-billable one blocks approval);
                    // Fuel Stop is allowed on stops over 15 min. The approver picks — nothing is guessed.
                    const reasonOptions = w.reasonChoices;
                    return (
                      <TableRow key={i} sx={{ backgroundColor: bg }}>
                        <TableCell sx={{ textAlign: "center", color: "text.disabled" }}>
                          {w.offerable ? (
                            <Checkbox
                              size="small"
                              checked={!!dec?.accept}
                              disabled={committed}
                              onChange={(e) => setWaitAccept(s.id, e.target.checked)}
                              sx={{ p: 0 }}
                            />
                          ) : (
                            "◦"
                          )}
                        </TableCell>
                        <TableCell colSpan={7} sx={{ py: 0.75 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                            <PlaceIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                            <Chip size="small" color={c.color} label={c.label} />
                            <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmtStopTime(s.suggestedStartTimeUtc)}</Typography>
                            <Typography variant="caption" color="text.secondary">· {stopDuration(Number(s.originalDurationMinutes))}</Typography>
                            {w.midTrip && <Chip size="small" variant="outlined" label="mid-trip" sx={{ height: 18, fontSize: "0.62rem" }} />}
                            {w.offerable ? (
                              <Select
                                size="small"
                                variant="standard"
                                displayEmpty
                                value={dec?.reason ?? ""}
                                disabled={committed}
                                onChange={(e) => setWaitReason(s.id, e.target.value as string)}
                                sx={{ minWidth: 170, fontSize: "0.75rem" }}
                              >
                                <MenuItem value="" sx={{ fontSize: "0.8rem", fontStyle: "italic", color: "text.secondary" }}>
                                  — Select billable reason —
                                </MenuItem>
                                {reasonOptions.map((r) => (
                                  <MenuItem key={r} value={r} sx={{ fontSize: "0.8rem" }}>{r}</MenuItem>
                                ))}
                              </Select>
                            ) : accepted ? (
                              <Chip size="small" variant="outlined" color="success" label="Already a wait" />
                            ) : dismissedAlready ? (
                              <Chip size="small" variant="outlined" label="Dismissed" />
                            ) : w.covered ? (
                              <Chip size="small" variant="outlined" label="Already logged" />
                            ) : isParkedCategory(w.category) ? (
                              <Chip size="small" variant="outlined" label="Parked — not billed" />
                            ) : (
                              <Chip size="small" variant="outlined" label="Not billed" />
                            )}
                            <Typography
                              variant="caption"
                              color={w.offerable && dec?.accept ? "primary" : "text.disabled"}
                              sx={{ ml: "auto", fontWeight: w.offerable ? 700 : 400, fontVariantNumeric: "tabular-nums" }}
                            >
                              {w.offerable
                                ? dec?.accept ? "will add" : "will dismiss"
                                : w.confidence != null ? `${Math.round(w.confidence * 100)}%` : ""}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  if (row.kind === "stop") {
                    const s = row.stop;
                    const c = categoryStyle(s.category);
                    const status = stopStatus(s);
                    const where = s.poiName
                      ? s.poiName
                      : s.distanceToPointMiles != null
                        ? `${s.distanceToPointMiles.toFixed(1)} mi from dispatched point`
                        : null;
                    return (
                      <TableRow key={i} sx={{ backgroundColor: status === "missed" ? CHANGE_BG : "rgba(0,0,0,0.025)" }}>
                        <TableCell sx={{ color: "text.disabled", textAlign: "center" }}>◦</TableCell>
                        <TableCell colSpan={7} sx={{ py: 0.75 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                            <PlaceIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                            <Chip size="small" color={c.color} label={c.label} />
                            <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmtStopTime(s.startUtc)}</Typography>
                            <Typography variant="caption" color="text.secondary">· {stopDuration(s.dwellMinutes)}</Typography>
                            {where && <Typography variant="caption" color="text.secondary">· {where}</Typography>}
                            {status === "missed" ? (
                              <Chip size="small" color="warning" label="Not logged" />
                            ) : status === "logged" ? (
                              <Chip size="small" variant="outlined" label="Logged" />
                            ) : null}
                            <Typography variant="caption" color="text.disabled" sx={{ ml: "auto", fontVariantNumeric: "tabular-nums" }}>
                              {Math.round((s.confidence ?? 0) * 100)}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  if (row.kind === "removed") {
                    const rw = row.removed;
                    return (
                      <TableRow key={i} sx={{ backgroundColor: FLAG_BG }}>
                        <TableCell sx={{ color: REMOVE_INK, textAlign: "center" }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </TableCell>
                        <TableCell colSpan={7} sx={{ py: 0.75 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                            <Chip size="small" variant="outlined" label="Wait removed" sx={{ color: REMOVE_INK, borderColor: REMOVE_INK, fontWeight: 700 }} />
                            <Typography variant="caption" sx={{ textDecoration: "line-through", opacity: 0.75, fontVariantNumeric: "tabular-nums" }}>
                              {fmtTime(rw.startLocal)}–{moment(rw.endLocal).format("HH:mm")}
                            </Typography>
                            {rw.reason && <Typography variant="caption" color="text.secondary">· {rw.reason}</Typography>}
                            <Typography variant="caption" color="text.secondary">
                              Removed due to re-time — GPS puts the van elsewhere at this time, so the driver's wait can't stand.
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  const b = row.ev;
                  const inserted = b.change === "inserted";
                  const changed = b.change === "odometer";
                  const flagged = b.line != null && flaggedLines.has(b.line);
                  const type = SHORT_NAME_TO_TYPE[b.eventTypeName];
                  const retimed = !inserted && type != null ? retimeByType.get(type) : undefined;
                  const driver = inserted || retimed ? undefined : driverByKey.get(keyOf(b));
                  // A re-point moved this event to a different point-of-service — call it out on the GPS side.
                  const locChanged =
                    !inserted &&
                    driver?.routeLocation != null &&
                    b.routeLocation != null &&
                    driver.routeLocation !== b.routeLocation;
                  return (
                    <TableRow
                      key={i}
                      sx={{
                        backgroundColor: flagged ? FLAG_BG : inserted ? INSERT_BG : retimed ? CHANGE_BG : undefined,
                        ...(flagged && { boxShadow: `inset 3px 0 0 ${FLAG_INK}` }),
                      }}
                    >
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          color: flagged ? FLAG_INK : "text.disabled",
                          fontWeight: flagged ? 700 : 400,
                        }}
                      >
                        {b.line ?? ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {inserted && <span style={{ color: INSERT_INK, fontWeight: 800 }}>＋ </span>}
                        {retimed && <ScheduleIcon sx={{ fontSize: 14, color: CHANGE_INK, mr: 0.5, verticalAlign: "-2px" }} />}
                        {displayEventName(b.eventTypeName)}
                        {(b.eventTypeName === "Wait Start" || b.eventTypeName === "Wait End") && b.comments && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
                            · {b.comments}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: inserted ? "text.disabled" : undefined, whiteSpace: "nowrap" }}>
                        {inserted ? (
                          "—"
                        ) : retimed ? (
                          <span style={{ textDecoration: "line-through", opacity: 0.6 }}>{fmtLocalFromUtc(retimed.driverTimeUtc)}</span>
                        ) : (
                          fmtTime(b.timeLocal)
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: inserted || retimed ? "text.disabled" : undefined }}>
                        {inserted || retimed ? "—" : fmtOdo(driver?.odometer)}
                      </TableCell>
                      <TableCell sx={{ color: inserted ? "text.disabled" : "text.secondary", whiteSpace: "nowrap", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {inserted ? "—" : (driver?.routeLocationName ?? b.routeLocationName ?? "—")}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          borderLeft: "1px solid rgba(0,0,0,0.12)",
                          whiteSpace: "nowrap",
                          backgroundColor: retimed ? CHANGE_BG : undefined,
                          color: retimed ? CHANGE_INK : undefined,
                          fontWeight: retimed ? 700 : undefined,
                        }}
                      >
                        {fmtTime(b.timeLocal)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          backgroundColor: inserted || changed ? CHANGE_BG : undefined,
                          color: inserted || changed ? CHANGE_INK : undefined,
                          fontWeight: inserted || changed ? 600 : undefined,
                        }}
                      >
                        {changed && driver?.odometer != null && (
                          <span style={{ textDecoration: "line-through", opacity: 0.6, marginRight: 6, fontSize: "0.72rem" }}>
                            {fmtOdo(driver.odometer)}
                          </span>
                        )}
                        {fmtOdo(b.odometer)}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          backgroundColor: locChanged ? CHANGE_BG : undefined,
                          color: locChanged ? CHANGE_INK : "text.secondary",
                          fontWeight: locChanged ? 700 : undefined,
                        }}
                      >
                        {locChanged && driver?.routeLocationName && (
                          <span style={{ textDecoration: "line-through", opacity: 0.6, marginRight: 6, fontSize: "0.72rem" }}>
                            {driver.routeLocationName}
                          </span>
                        )}
                        {b.routeLocationName ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: "wrap" }} alignItems="center">
            <LegendSwatch bg={INSERT_BG} label="Inserted from GPS" />
            <LegendSwatch bg={CHANGE_BG} label="Moved / odometer re-sourced" />
            <LegendSwatch bg="rgba(0,0,0,0.025)" label="GPS stop" />
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              Odometer in whole miles · times local
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      {/* Apply confirmation — restate exactly what will be written, since this is the one irreversible step. */}
      <Dialog open={confirmOpen} onClose={() => !applying && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Apply these fixes to trip {trip?.tripNumber ?? trip?.id}?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This writes the changes to the trip and runs the auto-approver for real. It isn't a preview.
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              {removeIds.size > 0 && <Row ink={REMOVE_INK} label={`Remove ${removeIds.size} event${removeIds.size === 1 ? "" : "s"}`} />}
              {retimeIds.size > 0 && <Row ink={CHANGE_INK} label={`Re-time ${retimeIds.size} event${retimeIds.size === 1 ? "" : "s"}`} />}
              {addKeys.size > 0 && <Row ink={INSERT_INK} label={`Add ${addKeys.size} event${addKeys.size === 1 ? "" : "s"} from GPS`} />}
              {repointIds.size > 0 && <Row ink={CHANGE_INK} label={`Re-point ${repointIds.size} event${repointIds.size === 1 ? "" : "s"} to the GPS-confirmed location`} />}
              {waitPayload.accepts.length > 0 && <Row ink={INSERT_INK} label={`Add ${waitPayload.accepts.length} GPS wait${waitPayload.accepts.length === 1 ? "" : "s"} (creates Wait Start/End pairs)`} />}
              {waitPayload.dismisses.length > 0 && <Row ink={REMOVE_INK} label={`Dismiss ${waitPayload.dismisses.length} GPS wait suggestion${waitPayload.dismisses.length === 1 ? "" : "s"}`} />}
              <Row ink={GPS_INK} label="Re-source odometers + recover any missing destination/end" />
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color={pass ? "success.main" : "warning.main"} sx={{ fontWeight: 700 }}>
              {pass
                ? "Projected: the trip will auto-approve and close."
                : preview.approveBlockReason
                  ? `Won't auto-approve: ${preview.approveBlockReason} The fixes still save; approve it in the Closer.`
                  : `Projected: still blocked — ${preview.outcome?.failReason || "a closer decision is needed"}.`}
            </Typography>
            {applyError && <Alert severity="error" sx={{ mt: 1.5 }}>{applyError}</Alert>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying} color="inherit">Cancel</Button>
          <Button
            onClick={applyChanges}
            variant="contained"
            disableElevation
            disabled={applying}
            startIcon={applying ? <CircularProgress size={15} color="inherit" /> : <SaveAltIcon />}
            sx={{ fontWeight: 700 }}
          >
            {applying ? "Applying…" : "Apply & save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Row = ({ ink, label }: { ink: string; label: string }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: ink, flexShrink: 0 }} />
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const LegendSwatch = ({ bg, label }: { bg: string; label: string }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ width: 22, height: 13, borderRadius: "3px", backgroundColor: bg, border: "1px solid rgba(0,0,0,0.15)" }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

export default AutoClosePreviewLayout;
